import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Avatar, Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CakeIcon from '@mui/icons-material/Cake';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Horse } from '@phosphor-icons/react';
import { Bar, Doughnut } from 'react-chartjs-2';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx, smartPageSx } from '../../theme/smartStableTheme';
import { todayDateInput } from '../../utils/dateUtils';

Chart.register(ChartDataLabels);

const CHART_COLORS = [COLORS.gold, COLORS.blue, COLORS.green, COLORS.rose, '#9a8f78', '#c7b783', '#8aa7a0'];

const COPY = {
  ar: {
    title: 'إحصائياتي',
    subtitle: 'قراءة منظمة لبيانات خيلك: اللون، السلالة، المنشأ، العمر، الجنس، والسكن.',
    totalHorses: 'إجمالي الخيل', totalCosts: 'إجمالي التكاليف', averageAge: 'متوسط العمر',
    accommodated: 'الخيل المسكنة', withRfid: 'لها شريحة',
    colors: 'توزيع الألوان', breeds: 'توزيع السلالات', origins: 'بلد المنشأ', ageGroups: 'الفئات العمرية',
    gender: 'الجنس', accommodation: 'حالة السكن', serviceMix: 'لمحة الخدمات',
    young: 'أقل من 3 سنوات', adult: '3 إلى 9 سنوات', senior: '10 سنوات فأكثر', unknown: 'غير محدد',
    mares: 'إناث', stallions: 'ذكور', accommodatedLabel: 'مسكنة', notAccommodated: 'غير مسكنة',
    years: 'سنة', mostFrequent: 'الأكثر تكرارًا فقط',
    noData: 'لا توجد بيانات كافية للعرض', noLink: 'هذا الحساب غير مربوط بمالك.',
  },
  en: {
    title: 'My Statistics',
    subtitle: 'A horse-first view by color, breed, origin, age, gender, and accommodation.',
    totalHorses: 'Total horses', totalCosts: 'Total costs', averageAge: 'Average age',
    accommodated: 'Accommodated horses', withRfid: 'With RFID',
    colors: 'Color distribution', breeds: 'Breed distribution', origins: 'Country of origin', ageGroups: 'Age groups',
    gender: 'Gender', accommodation: 'Accommodation status', serviceMix: 'Service snapshot',
    young: 'Under 3 years', adult: '3 to 9 years', senior: '10+ years', unknown: 'Unknown',
    mares: 'Mares', stallions: 'Stallions', accommodatedLabel: 'Accommodated', notAccommodated: 'Not accommodated',
    years: 'years', mostFrequent: 'Most frequent only',
    noData: 'Not enough data to show', noLink: 'This account is not linked to an owner profile.',
  },
};

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatMoney = (value) => `${formatNumber(value)} ${'SAR'}`;

const calcAge = (dob) => {
  if (!dob) return null;
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age -= 1;
  return Math.max(age, 0);
};
const pickLabel = (horse, enKey, arKey, lang, fallback) =>
  (lang === 'ar' ? horse[arKey] || horse[enKey] : horse[enKey] || horse[arKey]) || fallback;

const serviceLabel = (service, lang) => {
  const labels = {
    ar: { vetclinic: 'العيادة', farrier: 'البيطار', transportation: 'النقل', accommodation: 'السكن' },
    en: { vetclinic: 'Vet clinic', farrier: 'Farrier', transportation: 'Transport', accommodation: 'Accommodation' },
  };
  return labels[lang]?.[service] || service || '-';
};

function chartOptions({ horizontal = false } = {}) {
  return {
    indexAxis: horizontal ? 'y' : 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
      datalabels: { color: COLORS.text, anchor: 'end', align: 'end', clamp: true, font: { weight: '700', size: 11 }, formatter: (v) => (Number(v) > 0 ? formatNumber(v) : '') },
    },
    scales: {
      x: { grid: { color: '#eee7d8' }, ticks: { color: COLORS.muted, precision: 0 } },
      y: { grid: { display: false }, ticks: { color: COLORS.muted } },
    },
  };
}
function doughnutOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 10, color: COLORS.text, usePointStyle: true } },
      datalabels: { color: '#fff', font: { weight: '800', size: 11 }, formatter: (v) => (Number(v) > 0 ? formatNumber(v) : '') },
    },
  };
}

// ===== مكوّنات العرض (مطابقة لصفحة الإحصائيات الرئيسية) =====
function ResponsiveGrid({ children, min = 260, sx }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 2, alignItems: 'stretch', ...sx }}>{children}</Box>;
}
function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2.2, minHeight: 118 }}>
      <Stack direction="row" spacing={1.6} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 48, height: 48 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900, mt: 0.3 }}>{value}</Typography>
          {helper ? <Typography variant="caption" sx={{ color: COLORS.muted }}>{helper}</Typography> : null}
        </Box>
      </Stack>
    </Paper>
  );
}
function SectionCard({ title, subtitle, children, sx }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 320, ...sx }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{title}</Typography>
      {subtitle ? <Typography variant="caption" sx={{ color: COLORS.muted }}>{subtitle}</Typography> : null}
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Paper>
  );
}
function DistributionList({ rows, total, emptyLabel, money = false }) {
  if (!rows.length) return <Typography sx={{ color: COLORS.muted, py: 3 }}>{emptyLabel}</Typography>;
  return (
    <Stack spacing={1.2}>
      {rows.map(([label, value], index) => {
        const percent = total > 0 ? (Number(value) / total) * 100 : 0;
        return (
          <Box key={`${label}-${index}`}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography noWrap sx={{ color: COLORS.text, fontWeight: 800 }}>{label}</Typography>
              <Typography sx={{ color: COLORS.text, fontWeight: 900, whiteSpace: 'nowrap' }}>
                {money ? formatMoney(value) : formatNumber(value)}
                {!money ? <Typography component="span" variant="caption" sx={{ color: COLORS.muted }}> ({percent.toFixed(1)}%)</Typography> : null}
              </Typography>
            </Stack>
            <Box sx={{ height: 8, bgcolor: COLORS.cardSoft, borderRadius: 99, mt: 0.6 }}>
              <Box sx={{ height: '100%', width: `${Math.max(4, percent)}%`, bgcolor: CHART_COLORS[index % CHART_COLORS.length], borderRadius: 99 }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

const ClientHorseStats = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];
  const ownerId = localStorage.getItem('owner_id');
  const [horses, setHorses] = useState([]);
  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!ownerId || ownerId === 'null' || ownerId === 'undefined') {
        setError(text.noLink);
        setLoading(false);
        return;
      }
      try {
        const to = todayDateInput();
        const [horsesRes, statementRes] = await Promise.all([
          api.get(`/owners/${ownerId}/horses-with-details`),
          api.get(`/accounting-reports/owner-statements?owner_id=${ownerId}&from=2020-01-01&to=${to}`),
        ]);
        if (!alive) return;
        setHorses(Array.isArray(horsesRes.data) ? horsesRes.data : []);
        setStatement(Array.isArray(statementRes.data) ? statementRes.data : []);
      } catch {
        if (alive) setError(text.noData);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [ownerId, text.noData, text.noLink]);

  const summary = useMemo(() => {
    const totalCosts = statement.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const countBy = (getKey) => {
      const map = horses.reduce((acc, horse) => {
        const key = getKey(horse) || text.unknown;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});
      return Object.entries(map).sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0]))); // [label, value][]
    };

    const ages = horses.map((h) => calcAge(h.date_of_birth)).filter((a) => a !== null);
    const averageAge = ages.length ? ages.reduce((s, a) => s + a, 0) / ages.length : 0;
    const ageRows = [
      [text.young, ages.filter((a) => a < 3).length],
      [text.adult, ages.filter((a) => a >= 3 && a < 10).length],
      [text.senior, ages.filter((a) => a >= 10).length],
      [text.unknown, horses.length - ages.length],
    ].filter((r) => r[1] > 0);

    const byService = statement.reduce((map, row) => {
      const key = row.service_type || 'service';
      map[key] = (map[key] || 0) + Number(row.amount || 0);
      return map;
    }, {});
    const serviceRows = Object.entries(byService).sort((a, b) => b[1] - a[1]).map(([s, v]) => [serviceLabel(s, lang), v]);
    const accommodated = horses.filter((h) => h.room_name || h.stable_name).length;

    return {
      totalCosts, averageAge, accommodated,
      withRfid: horses.filter((h) => h.rfid_chip_number).length,
      colorRows: countBy((h) => pickLabel(h, 'color_english', 'color_arabic', lang, text.unknown)),
      breedRows: countBy((h) => pickLabel(h, 'breed_english', 'breed_arabic', lang, text.unknown)).slice(0, 8),
      originRows: countBy((h) => pickLabel(h, 'country_english', 'country_arabic', lang, text.unknown)).slice(0, 8),
      genderRows: countBy((h) => (h.gender === 'M' ? text.mares : h.gender === 'S' ? text.stallions : text.unknown)),
      accommodationRows: [
        [text.accommodatedLabel, accommodated],
        [text.notAccommodated, horses.length - accommodated],
      ].filter((r) => r[1] > 0),
      ageRows,
      serviceRows,
    };
  }, [horses, lang, statement, text]);

  if (loading) {
    return (
      <Box sx={{ ...smartPageSx, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const total = horses.length;

  return (
    <Box dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ ...smartPageSx, px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5, background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)' }}>
          <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>{text.title}</Typography>
          <Typography sx={{ color: COLORS.muted, mt: 0.6 }}>{text.subtitle}</Typography>
        </Paper>

        {error ? <Alert severity="warning" sx={{ mb: 2.5 }}>{error}</Alert> : null}

        {/* بطاقات المؤشرات */}
        <ResponsiveGrid min={220} sx={{ mb: 2.5 }}>
          <MetricCard icon={<Horse size={26} weight="fill" />} title={text.totalHorses} value={formatNumber(total)} />
          <MetricCard icon={<CakeIcon />} title={text.averageAge} value={summary.averageAge.toFixed(1)} helper={text.years} color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<ReceiptLongIcon />} title={text.withRfid} value={formatNumber(summary.withRfid)} color={COLORS.green} soft={COLORS.greenSoft} />
          <MetricCard icon={<HomeWorkIcon />} title={text.accommodated} value={formatNumber(summary.accommodated)} helper={`/ ${formatNumber(total)}`} color={COLORS.warning} soft={COLORS.warningSoft} />
          <MetricCard icon={<AccountBalanceWalletIcon />} title={text.totalCosts} value={formatMoney(summary.totalCosts)} color={COLORS.rose} soft={COLORS.roseSoft} />
        </ResponsiveGrid>

        {/* الرسوم البيانية + التوزيعات */}
        <ResponsiveGrid min={340} sx={{ mb: 2.5 }}>
          <SectionCard title={text.gender}>
            <Box sx={{ height: 260 }}>
              <Doughnut data={{ labels: summary.genderRows.map(([l]) => l), datasets: [{ data: summary.genderRows.map(([, v]) => v), backgroundColor: CHART_COLORS }] }} options={doughnutOptions()} />
            </Box>
          </SectionCard>

          <SectionCard title={text.ageGroups}>
            <Box sx={{ height: 260 }}>
              <Bar data={{ labels: summary.ageRows.map(([l]) => l), datasets: [{ data: summary.ageRows.map(([, v]) => v), backgroundColor: COLORS.blue }] }} options={chartOptions()} />
            </Box>
          </SectionCard>

          <SectionCard title={text.colors}>
            <DistributionList rows={summary.colorRows} total={total} emptyLabel={text.noData} />
          </SectionCard>
        </ResponsiveGrid>

        <ResponsiveGrid min={340} sx={{ mb: 2.5 }}>
          <SectionCard title={text.breeds} subtitle={text.mostFrequent}>
            <Box sx={{ height: 300 }}>
              <Bar data={{ labels: summary.breedRows.map(([l]) => l), datasets: [{ data: summary.breedRows.map(([, v]) => v), backgroundColor: COLORS.gold }] }} options={chartOptions({ horizontal: true })} />
            </Box>
          </SectionCard>

          <SectionCard title={text.origins} subtitle={text.mostFrequent}>
            <Box sx={{ height: 300 }}>
              <Bar data={{ labels: summary.originRows.map(([l]) => l), datasets: [{ data: summary.originRows.map(([, v]) => v), backgroundColor: COLORS.green }] }} options={chartOptions({ horizontal: true })} />
            </Box>
          </SectionCard>

          <SectionCard title={text.accommodation}>
            <DistributionList rows={summary.accommodationRows} total={total} emptyLabel={text.noData} />
          </SectionCard>
        </ResponsiveGrid>

        <ResponsiveGrid min={340}>
          <SectionCard title={text.serviceMix} sx={{ gridColumn: { lg: 'span 2' } }}>
            <DistributionList rows={summary.serviceRows} total={summary.totalCosts} emptyLabel={text.noData} money />
          </SectionCard>
        </ResponsiveGrid>
      </Box>
    </Box>
  );
};

export default ClientHorseStats;
